import React, { useEffect, useState } from "react";
import {
  Flex,
  Button,
  Box,
  Input,
  chakra,
  FormControl,
  FormErrorMessage,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { Formik, FormikHelpers, Field, FieldProps } from "formik";
import { CpfValidation } from "./CpfValidation";
import { cpfFormatter } from "@/utils/cpfFormatter";
import { useProposalStore } from "@/contexts/useProposalStore";
import {
  getLastConsignadoProposalByDocumentId,
} from "@/services/proposal.service";
import { useNavigate } from "react-router-dom";
import { Proposal } from "@/models/proposal.model";
import { ProposalConsignado } from "@/models/proposalConsignado.model";
import AntiFraudNotice from "@/components/AntiFraudNotice";
import { useWhiteLabelStore } from "@/contexts/whiteLabelContext";

interface LoginFormValues {
  cpf: string;
}

const ChakraForm = chakra("form");

const LoginFormConsignado: React.FC<{ shorthashId: string }> = ({ shorthashId }) => {
  const shorthash = shorthashId.replace("consig_", "");
  const { setProposal, clearProposal } = useProposalStore();
  const toast = useToast();
  const navigate = useNavigate();
  const getAssets = useWhiteLabelStore((s) => s.getAssets);
  const { name, primaryColor, secondaryColor } = getAssets();
  const [proposalData, setProposalData] = useState<
    Proposal | ProposalConsignado | null
  >(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isValidProposal, setIsValidProposal] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('ops, mensagem proposta não é valida');
  const [showAntiFraud, setShowAntiFraud] = useState<boolean>(false);

  const initialValues: LoginFormValues = {
    cpf: "",
  };

  const validateCpfMatch = (enteredCpf: string, proposalCpf: string): boolean => {
    const cleanEnteredCpf = enteredCpf.replace(/\D/g, "");
    const cleanProposalCpf = proposalCpf.replace(/\D/g, "");
    return cleanEnteredCpf === cleanProposalCpf;
  };

  useEffect(() => {
    const fetchProposalData = async () => {
      try {
        if (!shorthash) {
          throw new Error("shorthash is undefined");
        }

        // console.log("proposalType", proposalType);
        // console.log('typeProp', typeProp);
        const data = await getLastConsignadoProposalByDocumentId(shorthash);
        setProposalData(data);
        clearProposal();
        if (!data) {
          setIsValidProposal(false);
          toast({
            title: "Nenhuma proposta encontrada",
            description: "Não foi possível encontrar os dados da proposta.",
            status: "warning",
            duration: 3000,
            isClosable: true,
          });
        }
        setIsValidProposal(true);
      } catch (error: any) {
        setIsValidProposal(false);
        const errorMessage = error?.response?.data?.message ?? 'Erro ao buscar dados da proposta.'
        setErrorMessage(errorMessage)
        console.error("Erro ao buscar dados da proposta:", errorMessage);
        toast({
          title: "Erro",
          description: errorMessage,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
        // setProposalType(typeProp);
        // console.log("finaly proposalType", proposalType);
      }
    };

    fetchProposalData();
  }, [shorthash]);

  const handleSubmit = async (
    values: LoginFormValues,
    actions: FormikHelpers<LoginFormValues>
  ) => {
    const proposal = proposalData as ProposalConsignado;
    try {
      if (!proposalData) {
        toast({
          title: "Dados não carregados",
          description: "Os dados da proposta ainda não foram carregados.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const proposalCpf = proposal?.cpf || "";

      if (!validateCpfMatch(values.cpf, proposalCpf)) {
        toast({
          title: "CPF incorreto",
          description: "O CPF informado não corresponde ao CPF da proposta.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      setShowAntiFraud(true);
    } catch (error) {
      console.error("Erro no login:", error);
      toast({
        title: "Erro ao efetuar login",
        description: "Ocorreu um erro ao validar o CPF.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      actions.setSubmitting(false);
    }
  };

  if (!isValidProposal) {
    return (
      <Flex
        width="100%"
        minHeight="50vh"
        justifyContent="center"
        alignItems="center"
        backgroundColor="transparent"
        color="white"
        textAlign='center'
        px={10}
      >
       {errorMessage}
      </Flex>
    );
  }

  if (isLoading) {
    return (
      <Flex
        width="100%"
        minHeight="50vh"
        justifyContent="center"
        alignItems="center"
        backgroundColor="transparent"
        color="white"
      >
        <Spinner size="xl" />
      </Flex>
    );
  }
  
  const handleAntiFraudContinue = () => {
    setProposal(proposalData as ProposalConsignado);
    navigate(`/${shorthashId}/consignado/geolocalizacao`);
  };

  if (showAntiFraud) {
    return (
      <Flex
        position="fixed"
        top={0}
        left={0}
        width="100vw"
        height="100vh"
        zIndex={9999}
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        overflowY="auto"
        bgGradient={`linear(to-b, ${secondaryColor} 0%, #0b1f33 45%, #050d18 100%)`}
        fontFamily="Lexend Deca, sans-serif"
      >
        <AntiFraudNotice
          brandName={name}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          onContinue={handleAntiFraudContinue}
          contactPhone="11 3777-9282"
        />
      </Flex>
    );
  }

  if (isValidProposal) {
    return (
      <Flex
        width="100%"
        justifyContent="center"
        backgroundColor="transparent"
        flexDirection="column"
        color="white"
        alignItems="center"
        fontFamily="Lexend Deca"
        paddingX="5%"
      >
        <Formik
          initialValues={initialValues}
          validationSchema={CpfValidation}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting, handleSubmit }) => (
            <ChakraForm width="100%" onSubmit={handleSubmit}>
              <Box width="100%" paddingBottom="2rem">
                <FormControl isInvalid={!!errors.cpf && touched.cpf}>
                  <Field name="cpf">
                    {({ field, form }: FieldProps) => (
                      <Input
                        {...field}
                        id="cpf"
                        placeholder="Digite seu CPF"
                        type="tel"
                        onChange={(e) =>
                          form.setFieldValue(
                            "cpf",
                            cpfFormatter(e.target.value)
                          )
                        }
                        fontSize="lg"
                        width="100%"
                        variant="flushed"
                        focusBorderColor="primary"
                        textShadow="0 0 5px rgba(0, 0, 0, 0.5)"
                        _placeholder={{ color: "white" }}
                        paddingY="1rem"
                      />
                    )}
                  </Field>
                  <FormErrorMessage marginTop="0.5rem">
                    {errors.cpf}
                  </FormErrorMessage>
                </FormControl>
                <Button
                  backgroundColor="white"
                  width="100%"
                  color="primary"
                  borderRadius="24px"
                  type="submit"
                  height="3rem"
                  isDisabled={isSubmitting}
                  marginTop="1.5rem"
                  fontSize="lg"
                  paddingY="1.5rem"
                  _hover={{ backgroundColor: "gray.100" }}
                >
                  Inicie sua assinatura digital
                </Button>
              </Box>
            </ChakraForm>
          )}
        </Formik>
      </Flex>
    );
  }
};

export default LoginFormConsignado;
